<script>
import {mapActions, mapGetters} from "vuex";
import Keypoints from "@/components/election/Keypoints.vue";
import RichText from "@/components/ui/RichText.vue";
import {getNoDateElectionPlaceholderText} from "@/lib";
import LoadingSpinner from "@/components/layout/LoadingSpinner.vue";
export default {
  name: "Candidate",
  components: {RichText, Keypoints, LoadingSpinner},
  props: {
    candidateId: {
      type: Number,
      required: true
    },
    electionId: {
      type: Number,
      required: true
    }
  },
  methods: {
    ...mapActions('elections', ['loadCandidateDetails', 'clearDetailedCandidates']),
    getTextOrDefault(value) {
      if(value && value.trim().length > 0) {
        return value;
      }

      return getNoDateElectionPlaceholderText(this.lastUpdatedDate);
    }
  },
  computed: {
    ...mapGetters('elections', ['candidateDetails']),
    showDetails () {
      return this.candidateDetails.elected || !this.candidateDetails.election.complete;
    },
    lastUpdatedDate() {
      if(!this.candidateDetails) return;

      if(this.candidateDetails.person.lastUpdate) return  new Date(this.candidateDetails.person.lastUpdate);

      const electionUpdated = new Date(this.candidateDetails.election.updated_at);
      const candidateUpdated = new Date(this.candidateDetails.person.updated_at);

      return electionUpdated > candidateUpdated ? electionUpdated : candidateUpdated;
    },
    lastUpdated() {
      return this.lastUpdatedDate.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit"});
    },
    position() {
      if (!this.candidateDetails) return;

      if(this.candidateDetails.elected) {
        if(this.candidateDetails.person.gender === 'f') {
          if(this.candidateDetails.position === 'Landrat') {
            return 'Landrätin';
          } else if(this.candidateDetails.position === 'Bürgermeister') {
            return 'Bürgermeisterin';
          }
        }

        return this.candidateDetails.position;
      }
        return `${this.candidateDetails.position}-Kandidat${this.candidateDetails.person.gender === 'f' ? 'in' : ''}`;
    },
    communeName() {
      const { name, externalCommune } = this.candidateDetails.commune;

      if(name) {
        return name;
      } else {
        return externalCommune;
      }
    }
  },
  async mounted() {
    this.clearDetailedCandidates();
    await this.loadCandidateDetails({electionId: this.electionId, candidateId: this.candidateId});
  }
}
</script>

<template>
  <div class="px-5">
<v-row class="mt-7" v-if="candidateDetails">
  <v-col cols="12">
    <router-link to="/elections" style="font-size: 14px; margin-bottom: 8px">
      <v-icon small left color="primary">mdi-arrow-left</v-icon>
      Zurück
    </router-link>
  </v-col>
  <v-col cols="12">
    <div class="d-flex justify-space-between">
      <div style="display: flex; gap: 8px">
        <span v-if="candidateDetails.elected" class="candidate-badge elected-badge">Gewählt</span>
        <span class="candidate-badge" v-else>Kandidat/in</span>
        <span class="candidate-badge">{{ candidateDetails.election.type }}</span>
        <span class="candidate-badge">{{ candidateDetails.party.name }}</span>
      </div>
      <span>Stand: {{ lastUpdated }}</span>
    </div>
    <div class="custom-header-text mt-2">{{ candidateDetails.person.name }}</div>
    <p class="pt-2">{{ position }} {{ communeName }}</p>
  </v-col>
  <v-col :cols="showDetails ? 7 : 12">
    <v-sheet class="info-sheet">
      <p class="info-sheet-title">Politische Schwerpunkte von {{ candidateDetails.person.name }}</p>
      <keypoints :keypoints="candidateDetails.personalKeypoints" :last-updated-date="lastUpdatedDate" />
    </v-sheet>
    <v-sheet class="info-sheet" v-if="candidateDetails.partyKeypoints">
      <p class="info-sheet-title">Politische Schwerpunkte {{ candidateDetails.party.name }} {{ communeName }}</p>
      <keypoints :keypoints="candidateDetails.partyKeypoints" :last-updated-date="lastUpdatedDate" />
    </v-sheet>
    <v-sheet class="info-sheet" v-if="showDetails">
      <p class="info-sheet-title">Soft facts*</p>
      <rich-text :source="getTextOrDefault(candidateDetails.person.softFacts)" />
      <p style="font-size: 12px">*Öffentlich zugängliche Informationen auf Basis einer Internetrecherche, nicht offiziell verifizierte Angaben</p>
    </v-sheet>
  </v-col>
  <v-col cols="5" v-if="showDetails">
    <v-sheet class="contact-sheet">
      <p class="contact-sheet-title">{{ candidateDetails.person.name }}</p>
      <div v-if="candidateDetails.person.contact">
        <div v-if="candidateDetails.person.contact.mail">
          <v-icon small left>$mailIcon</v-icon> {{ candidateDetails.person.contact.mail }}
        </div>
        <div v-if="candidateDetails.person.contact.phone">
          <v-icon small left>$callIcon</v-icon>
          {{ candidateDetails.person.contact.phone }}
        </div>
        <div v-if="candidateDetails.person.contact.website">
          <v-icon small left color="primary">mdi-open-in-new</v-icon>
          <a :href="candidateDetails.person.contact.website" target="_blank">{{ candidateDetails.person.contact.website }}</a>
        </div>
        <div v-if="candidateDetails.person.contact.officePhone || candidateDetails.person.contact.officeMail" class="pt-5">
          <p class="contact-sheet-title">Vorzimmer</p>
          <div v-if="candidateDetails.person.contact.officeName">
            <v-icon small left color="primary">mdi-account</v-icon> {{ candidateDetails.person.contact.officeName }}
          </div>
          <div v-if="candidateDetails.person.contact.officeMail">
            <v-icon small left>$mailIcon</v-icon> {{ candidateDetails.person.contact.officeMail }}
          </div>
          <div v-if="candidateDetails.person.contact.officePhone">
            <v-icon small left>$callIcon</v-icon>
            {{ candidateDetails.person.contact.officePhone }}
          </div>
        </div>
      </div>
    </v-sheet>
    <v-sheet class="info-sheet">
      <p class="info-sheet-title">Wahlergebnis</p>
      <rich-text :source="getTextOrDefault(candidateDetails.person.specialFeatures)" />
    </v-sheet>
    <v-sheet class="info-sheet">
      <p class="info-sheet-title">Biografie</p>
      <div class="info-subtitle">Beruflicher Werdegang</div>
      <rich-text :source="getTextOrDefault(candidateDetails.person.biography.professionalCareer)" />
      <div class="info-subtitle mt-10">Bildungsweg</div>
      <rich-text :source="getTextOrDefault(candidateDetails.person.biography.educationalBackground)" />
    </v-sheet>
    <v-sheet class="info-sheet">
      <p class="info-sheet-title">Mitgliedschaften</p>
      <div class="info-subtitle">Lokale Mitgliedschaften</div>
      <rich-text :source="getTextOrDefault(candidateDetails.person.memberships.localMemberships)" />
      <div class="info-subtitle">Fachliche Mitgliedschaften</div>
      <rich-text :source="getTextOrDefault(candidateDetails.person.memberships.professionalMemberships)" />
    </v-sheet>
  </v-col>
</v-row>
    <loading-spinner v-else />
  </div>
</template>

<style scoped>
.info-sheet {
  background-color: #FFFFFF;
  border-radius: 5px;
  padding: 24px 18px;
  margin-bottom: 16px;
}

.contact-sheet {
  background-color: #F5DBE6;
  border-radius: 5px !important;
  overflow: hidden;
  padding: 24px 18px;
  margin-bottom: 16px;
}

.info-sheet-title {
  font-size: 18px;
  font-family: "GeometriaMedium", sans-serif;
  color: #B40055;
  margin-bottom: 24px;
}

.contact-sheet-title {
  font-size: 18px;
  font-family: "GeometriaMedium", sans-serif;
  margin-bottom: 8px;
}

.info-subtitle {
  font-size: 14px;
  font-family: "GeometriaMedium", sans-serif;
  background-color: #F2F2F3;
  padding: 4px;
  padding-bottom: 3px;
  margin-bottom: 16px;
}
</style>